import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, useTheme, useMediaQuery, Card, CardContent, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import CheckerApprovalModal from '../components/CheckerApprovalModal';
import { useToast } from '../components/ui/ToastProvider';
interface CheckerInboxProps {
  onLogout: () => void;
}
interface RecordData {
  id: number;
  fullName: string;
  username: string;
  email: string;
  mobile: string;
  currency: string;
  branchCode: string;
  settlementAccount: string;
  designation: string;
  status: string;
  batchId: string;
  fileName: string;
  submittedAt: string;
  approvalStatus: 'pending' | 'completed' | 'rejected';
}
export default function CheckerInbox({
  onLogout
}: CheckerInboxProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [records, setRecords] = useState<RecordData[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<RecordData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState<'complete' | 'reject'>('complete');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    showToast
  } = useToast();
  useEffect(() => {
    // Load records from localStorage
    const storedRecords = JSON.parse(localStorage.getItem('checkerInboxRecords') || '[]');
    setRecords(storedRecords.filter((r: RecordData) => r.approvalStatus === 'pending'));
  }, []);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  const handleOpenModal = (record: RecordData, action: 'complete' | 'reject') => {
    setSelectedRecord(record);
    setModalAction(action);
    setModalOpen(true);
  };
  const handleSubmitApproval = (reason: string) => {
    if (!selectedRecord) return;
    const allCheckerRecords = JSON.parse(localStorage.getItem('checkerInboxRecords') || '[]');
    // Update the record status in checker inbox
    const updatedCheckerRecords = allCheckerRecords.map((r: RecordData) => r.id === selectedRecord.id ? {
      ...r,
      approvalStatus: modalAction === 'complete' ? 'completed' : 'rejected',
      checkerReason: reason,
      checkedAt: new Date().toISOString()
    } : r);
    localStorage.setItem('checkerInboxRecords', JSON.stringify(updatedCheckerRecords));
    // Also update in maker inbox
    const allMakerRecords = JSON.parse(localStorage.getItem('makerInboxRecords') || '[]');
    const updatedMakerRecords = allMakerRecords.map((r: RecordData) => r.id === selectedRecord.id ? {
      ...r,
      approvalStatus: modalAction === 'complete' ? 'completed' : 'rejected',
      checkerReason: reason,
      checkedAt: new Date().toISOString()
    } : r);
    localStorage.setItem('makerInboxRecords', JSON.stringify(updatedMakerRecords));
    // Update local state
    setRecords(updatedCheckerRecords.filter((r: RecordData) => r.approvalStatus === 'pending'));
    showToast(`Record ${modalAction === 'complete' ? 'completed' : 'rejected'} successfully`, 'success');
    setModalOpen(false);
    setSelectedRecord(null);
  };
  return <Box sx={{
    width: '100%',
    height: '100%',
    bgcolor: 'background.default'
  }} data-id="element-2654">
      <TopBar onMenuClick={handleDrawerToggle} onLogout={onLogout} data-id="element-2655" />
      <Sidebar open={mobileOpen} onClose={handleDrawerToggle} activePage="checker-inbox" data-id="element-2656" />
      <Box component="main" sx={{
      flexGrow: 1,
      py: 2,
      px: 1,
      width: {
        xs: '100%',
        sm: `calc(100% - ${240}px)`
      },
      ml: {
        xs: 0,
        sm: `${240}px`
      },
      mt: 8
    }} data-id="element-2657">
        <Container maxWidth="xl" data-id="element-2658">
          <Grid container spacing={2} alignItems="center" sx={{
          mb: 3
        }} data-id="element-2659">
            <Grid item xs={12} data-id="element-2660">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }} data-id="element-2661">
                <Box sx={{
                display: 'flex',
                alignItems: 'center'
              }} data-id="element-2662">
                  <Icon icon="lucide:check-square" width={24} height={24} color="#0EA5E9" data-id="element-2663" />
                  <Typography variant="h5" component="h1" sx={{
                  ml: 1.5,
                  fontWeight: 600
                }} data-id="element-2664">
                    Checker Inbox
                  </Typography>
                </Box>
                <Chip label={`${records.length} Pending`} sx={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                color: '#3B82F6',
                fontWeight: 600
              }} data-id="element-2665" />
              </Box>
            </Grid>
          </Grid>
          {records.length === 0 ? <Card data-id="element-2666">
              <CardContent sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            textAlign: 'center'
          }} data-id="element-2667">
                <Icon icon="lucide:check-square" width={80} height={80} color="#CBD5E1" style={{
              marginBottom: 24
            }} data-id="element-2668" />
                <Typography variant="h6" color="text.secondary" sx={{
              mb: 1
            }} data-id="element-2669">
                  No Pending Records
                </Typography>
                <Typography variant="body2" color="text.secondary" data-id="element-2670">
                  Records awaiting checker approval will appear here
                </Typography>
              </CardContent>
            </Card> : <Card data-id="element-2671">
              <TableContainer component={Paper} variant="outlined" data-id="element-2672">
                <Table data-id="element-2673">
                  <TableHead data-id="element-2674">
                    <TableRow data-id="element-2675">
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2676">
                        Full Name
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2677">
                        Username
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2678">
                        Email
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2679">
                        Mobile
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2680">
                        Branch Code
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2681">
                        Batch
                      </TableCell>
                      <TableCell sx={{
                    fontWeight: 600
                  }} data-id="element-2682">
                        Submitted
                      </TableCell>
                      <TableCell align="center" sx={{
                    fontWeight: 600
                  }} data-id="element-2683">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-2684">
                    {records.map(record => <TableRow key={record.id} hover data-id="element-2685">
                        <TableCell data-id="element-2686">{record.fullName}</TableCell>
                        <TableCell data-id="element-2687">{record.username}</TableCell>
                        <TableCell data-id="element-2688">{record.email}</TableCell>
                        <TableCell data-id="element-2689">{record.mobile}</TableCell>
                        <TableCell data-id="element-2690">
                          <Chip label={record.branchCode} size="small" sx={{
                      backgroundColor: 'rgba(6, 182, 212, 0.1)',
                      color: '#06B6D4',
                      fontWeight: 600
                    }} data-id="element-2691" />
                        </TableCell>
                        <TableCell data-id="element-2692">
                          <Typography variant="body2" fontSize="0.85rem" data-id="element-2693">
                            {record.fileName}
                          </Typography>
                        </TableCell>
                        <TableCell data-id="element-2694">
                          <Typography variant="body2" fontSize="0.85rem" data-id="element-2695">
                            {new Date(record.submittedAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" data-id="element-2696">
                          <Box sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'center'
                    }} data-id="element-2697">
                            <Tooltip title="Complete" data-id="element-2698">
                              <IconButton size="small" onClick={() => handleOpenModal(record, 'complete')} sx={{
                          color: '#10B981',
                          '&:hover': {
                            backgroundColor: 'rgba(16, 185, 129, 0.1)'
                          }
                        }} data-id="element-2699">
                                <Icon icon="lucide:check-circle" width={20} height={20} data-id="element-2700" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject" data-id="element-2701">
                              <IconButton size="small" onClick={() => handleOpenModal(record, 'reject')} sx={{
                          color: '#EF4444',
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)'
                          }
                        }} data-id="element-2702">
                                <Icon icon="lucide:x-circle" width={20} height={20} data-id="element-2703" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>}
        </Container>
      </Box>
      {selectedRecord && <CheckerApprovalModal open={modalOpen} onClose={() => {
      setModalOpen(false);
      setSelectedRecord(null);
    }} record={selectedRecord} action={modalAction} onSubmit={handleSubmitApproval} data-id="element-2704" />}
    </Box>;
}